import { AnalyticsBrowser } from "@segment/analytics-next";
import { isPrerender } from "./isPrerender";

/**
 * Segment Analytics Configuration
 *
 * In PRODUCTION (Vercel): Uses /segment-api proxy to bypass ad-blockers.
 * In DEVELOPMENT/STAGING (Lovable Cloud): Uses standard Segment endpoints.
 *
 * Environment Variable: VITE_SEGMENT_WRITE_KEY (public, safe to expose)
 */

const SEGMENT_WRITE_KEY = import.meta.env.VITE_SEGMENT_WRITE_KEY as string | undefined;

// Lazy-loaded analytics instance
let analyticsInstance: AnalyticsBrowser | null = null;

/**
 * Eagerly initialize Segment so the instance is available before PostHog init.
 * Also assigns to window.analytics so PostHog can reference it.
 * Call this once at app startup (main.tsx).
 */
export function initSegment(): void {
  const instance = getAnalytics();
  if (instance) {
    (window as any).analytics = instance;
  }
}

/**
 * Run a callback after Segment is fully ready.
 */
export function onSegmentReady(callback: () => void): void {
  const instance = getAnalytics();

  if (!instance) {
    callback();
    return;
  }

  const ready = (instance as any).ready;
  if (typeof ready === "function") {
    ready.call(instance, callback);
  } else {
    callback();
  }
}

/**
 * Get or initialize the Segment analytics instance.
 * Returns null if VITE_SEGMENT_WRITE_KEY is not configured.
 */
function getAnalytics(): AnalyticsBrowser | null {
  if (isPrerender()) {
    // Skip during prerender (Puppeteer/headless) — analytics flushes keep
    // the network busy and pollute production data with CI page hits.
    return null;
  }

  if (!SEGMENT_WRITE_KEY) {
    if (import.meta.env.DEV) {
      console.warn("[Segment] VITE_SEGMENT_WRITE_KEY is not configured. Analytics disabled.");
    }
    return null;
  }

  if (!analyticsInstance) {
    const isProduction = import.meta.env.PROD;

    analyticsInstance = AnalyticsBrowser.load(
      {
        writeKey: SEGMENT_WRITE_KEY,
        ...(isProduction && {
          cdnURL: undefined,
        }),
      },
      {
        integrations: {
          "Segment.io": isProduction
            ? {
                apiHost: `${window.location.host}/segment-api/v1`,
                protocol: "https",
              }
            : {},
        },
      }
    );

    if (import.meta.env.DEV) {
      console.log(`[Segment] Initialized in ${isProduction ? "production" : "development"} mode`);
    }
  }

  return analyticsInstance;
}

// ============================================================================
// Typed Analytics Interface
// ============================================================================

export interface UserTraits {
  email?: string;
  created_at?: string;
  role?: string;
  company_id?: string;
  company_name?: string;
  subscription_tier?: string;
  full_name?: string;
  [key: string]: string | number | boolean | undefined;
}

export interface GroupTraits {
  name?: string;
  subscription_tier?: string;
  member_count?: number;
  [key: string]: string | number | boolean | undefined;
}

export interface TrackProperties {
  [key: string]: string | number | boolean | undefined | null;
}

/**
 * Identify a user with optional traits.
 * Call this when a user signs in.
 */
export function identify(userId: string, traits?: UserTraits): void {
  const analytics = getAnalytics();
  if (analytics) {
    analytics.identify(userId, traits);
    if (import.meta.env.DEV) {
      console.log("[Segment] identify:", userId, traits);
    }
  }
}

/**
 * Associate a user with a group (company/team) for B2B tracking.
 * Call this after identify when a user belongs to a company.
 */
export function group(groupId: string, traits?: GroupTraits): void {
  const analytics = getAnalytics();
  if (analytics) {
    analytics.group(groupId, traits);
    if (import.meta.env.DEV) {
      console.log("[Segment] group:", groupId, traits);
    }
  }
}

/**
 * Track an event with optional properties.
 */
export function track(eventName: string, properties?: TrackProperties): void {
  const analytics = getAnalytics();
  if (analytics) {
    analytics.track(eventName, properties);
    if (import.meta.env.DEV) {
      console.log("[Segment] track:", eventName, properties);
    }
  }
}

/**
 * Sensitive query parameters that should be stripped from analytics.
 */
const SENSITIVE_PARAMS = ["__lovable_token"];

/**
 * Track a page view with sanitized URL data.
 * Automatically strips sensitive query parameters like __lovable_token.
 */
export function page(category?: string, name?: string, properties?: TrackProperties): void {
  const analytics = getAnalytics();
  if (analytics) {
    // Parse and sanitize the current URL
    const url = new URL(window.location.href);
    SENSITIVE_PARAMS.forEach((param) => url.searchParams.delete(param));

    // Build sanitized properties - spread input first, then override with sanitized URL values
    // This ensures __lovable_token is stripped even if passed in properties
    const sanitizedProperties: TrackProperties = {
      ...properties,
      path: url.pathname,
      search: url.search,
      url: url.toString(),
      title: document.title,
    };

    // Call Segment page with optional category/name
    analytics.page(category ?? undefined, name ?? undefined, sanitizedProperties);
    
    if (import.meta.env.DEV) {
      // Log with cleaner format (use path as fallback display)
      const displayName = name || sanitizedProperties.path;
      console.log("[Segment] page:", displayName, sanitizedProperties);
    }
  }
}

/**
 * Reset the analytics state.
 * Call this when a user signs out to prevent cross-user data pollution.
 */
export function reset(): void {
  const analytics = getAnalytics();
  if (analytics) {
    analytics.reset();
    if (import.meta.env.DEV) {
      console.log("[Segment] reset");
    }
  }
}

/**
 * Convenience export for all analytics methods.
 */
export const analytics = {
  identify,
  group,
  track,
  page,
  reset,
};

export default analytics;
